import {
  DataRoomAccessPermission,
  DataRoomListItem,
  DataRoomStatus,
  SpaceKind,
  SpaceNodeKind,
  SpaceNodeSortBy,
  TreeSpaceKind,
  VaultSpaceView,
  WorkspaceListItem,
} from 'shared';

import { StringKey } from '../../../lang/index';
import { Order } from '../../../types/general.types';

export enum DataRoomTab {
  ACTIVITY = 'activity',
  WORKSPACES = 'workspaces',
}

export enum WorkspacesNavId {
  HOME = 'home',
}

export enum WorkspaceCardMenuAction {
  DELETE = 'delete',
  DOWNLOAD = 'download',
  SETTINGS = 'settings',
  SHARE_ACCESS = 'share-access',
  VIEW = 'view',
}

export enum WorkspaceCardStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  LOCKED = 'locked',
}

export type WorkspaceCardStat = {
  label: StringKey;
  unit?: StringKey;
  value: number | string;
};

// A folder's counts, size and date, where its space reads them; null where it does not.
export type SpaceBrowserFolder = {
  id: string;
  isShared?: boolean;
  itemCount: null | number;
  name: string;
  size: null | number;
  updatedAt: null | string;
};

export type SpaceBrowserFile = {
  contentType: string;
  createdAt: string;
  // Why the file may be neither renamed nor deleted, shown on those actions; absent when it may.
  editLockReason?: StringKey;
  folderId: string;
  id: string;
  isDocusignSynced?: boolean;
  name: string;
  size: number;
  updatedAt: string;
};

// The columns a browser table can be ordered by: name, last edit or size, in a space and in the Vault.
export type SpaceBrowserSort = { by: SpaceNodeSortBy; order: Order };

export type SpaceBrowserPermissions = {
  canCreateFolder: boolean;
  canDownload: boolean;
  // The owner's tools — settings, Manage Access, upload, new folder; none for a recipient.
  canManage: boolean;
  // Uploading, deleting and creating folders.
  canWrite: boolean;
  isReadOnly: boolean;
};

// The "Load more" / "Show less" control under the browser table, one object so it passes whole.
export type SpaceBrowserPagination = {
  // Every page is in and there was more than one: the control folds back to the first.
  canShowLess: boolean;
  isLoadingMore: boolean;
  // The rows shown stand in for a re-sort on its way: the sizes are the old rows', and the control
  // stays in place but cannot be used.
  isReloading: boolean;
  loadMore: () => void;
  // Items the next page holds; 0 once the whole folder is shown.
  nextPageSize: number;
  // Items not loaded yet: with the rows in view, the folder's whole size.
  remainingCount: number;
  showLess: () => void;
};

// A folder or a file of the browser table, as a row action names it.
export type SpaceBrowserNode = { id: string; kind: SpaceNodeKind; name: string };

export type WorkspaceBrowserViewProps = {
  isRecipientView: boolean;
  selection: SpaceSelection;
};

export type BrowserNodeActions = {
  onDeleteFile?: (file: SpaceBrowserNode) => void;
  onDeleteFolder?: (folder: SpaceBrowserNode) => void;
  onDownloadFile?: (fileId: string) => void;
  onDownloadFolder?: (folderId: string) => void;
  // Every file has a Preview, and the viewer names what it is showing — so the name travels with
  // the id rather than being looked up again from a row the viewer never sees.
  onOpenFile?: (fileId: string, fileName: string) => void;
  onRenameFile?: (file: SpaceBrowserNode) => void;
  onRenameFolder?: (folder: SpaceBrowserNode) => void;
};

export type SpaceBrowserCrumb = {
  id: string;
  name: string;
};

export type HomeSpace = DataRoomListItem | VaultSpaceView | WorkspaceListItem;

/**
 * A room on the Home of somebody it was shared with.
 *
 * Not a `HomeSpace`: the owner's card counts how many people were invited and how long they spent
 * inside, and the route that answers a recipient deliberately says none of it. What is left is
 * what the room holds, which they were invited to read anyway.
 */
export type RecipientRoomView = {
  companyName: string;
  createdAt: string;
  description: null | string;
  id: string;
  metrics: {
    fileCount: number;
    folderCount: number;
    lastActivityAt: null | string;
    totalSize: number;
  };
  name: string;
  permission: DataRoomAccessPermission;
  status: DataRoomStatus;
  updatedAt: string;
};

export type SelectedSpace = {
  createdAt: null | string;
  id: string;
  isLocked: boolean;
  kind: SpaceKind;
  name: string;
  permission?: DataRoomAccessPermission;
  status: DataRoomStatus | null;
};

// The tree loads lazily, so a folder's path cannot be looked up; it travels with the selection.
export type SpaceSelection = {
  breadcrumbs: SpaceBrowserCrumb[];
  folderId: null | string;
  space: SelectedSpace;
};

// A space as the routes address it: which kind it is, and its id.
export type SpaceRef = { id: string; kind: SpaceKind };

// A workspace or a data room — the kinds whose folders form a tree of their own.
export type TreeSpaceRef = { id: string; kind: TreeSpaceKind };
